<template>
  <default-textual-section v-if="$store.state.displayType === 'textual'" :menu="menu" :section="section"
                           :section-modifier="sectionModifier" :menu-item-component="menuItemComponent"
                           content-area-top-layout=""
                           content-area-bottom-layout=""
                           :is-tag-legend-hidden="'steak' === sectionModifier"
                           class="menu--section-wv_steak_house" :class="containerClasses"></default-textual-section>
  <default-visual-section v-else :menu="menu" :section="section"
                          :section-modifier="sectionModifier" :menu-item-component="menuItemComponent"
                          content-area-top-layout=""
                          content-area-bottom-layout=""
                          :is-tag-legend-hidden="'steak' === sectionModifier"
                          class="menu--section-wv_steak_house" :class="containerClasses"></default-visual-section>
</template>

<script lang="ts">
/**
 * Custom component for WV Steak House default menu sections.
 *
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 2.0.0
 * @copyright  Copyright (C) 2023-2025, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 *  2023-08-29 (1.0.0) (jhoward)
 *    - Copy from <Bimini_defaultSection> component to <WvSteakHouseDefaultSection> component.
 *
 *  2023-11-15 (1.0.1) (jhoward)
 *    - Fix meat temperature line break location in <WvSteakHouseDefaultSection>.
 *
 *  2025-02-10 (1.1.0) (jhoward)
 *    - Add <menu-schedules> to the <WvSteakHouseDefaultSection> component if in admin view.
 *
 *  2025-04-08 (2.0.0) (jhoward)
 *    - Use <default-textual-section> and <default-visual-section> in <WvSteakHouseDefaultSection>.
 *    - Add textual and visual menu CSS tweaks to <WvSteakHouseDefaultSection>.
 */

import {defineComponent, PropType} from "vue";
import {ALLOWED_ITEM_COMPONENTS, Menu, MENU_ITEM_TAG_LOCATIONS, MenuSection} from "../defs";
import DefaultVisualSection from "./visual/defaultVisualSection.vue";
import DefaultTextualSection from "./textual/defaultTextualSection.vue";

export default defineComponent({
  components: {
    DefaultTextualSection, DefaultVisualSection,
  },
  name: "WvSteakHouseDefaultSection",
  props: {
    section: {
      type: Object as PropType<MenuSection>,
      required: true
    },
    menu: {
      type: Object as PropType<Menu>,
      required: true
    },
    menuItemComponent: {
      type: String,
      default: '',
      validate: (value: string) => '' === value || ALLOWED_ITEM_COMPONENTS.indexOf(value)
    },
    menuItemTagLocation: {
      type: String as PropType<MENU_ITEM_TAG_LOCATIONS>,
      default: ''
    },
    sectionModifier: {
      type: String,
      default: ''
    },
  },
  computed: {
    containerClasses(): { [key: string]: boolean } {
      return {
        'menu--section-meat_temperature': this.isMeatTemperatureSection
      };
    },
    isMeatTemperatureSection(): boolean {
      return /(^|\s)meat temperatures?(\s|$)/i.test(this.section.ms_title);
    },
  },
})
</script>

<style>
html .menu--container-the_steak_house {
  margin: 0 auto;
  font-family: arno-pro, serif;
  background-image: url('/library/images/menu/background-wv-steak-house.jpg');
  background-size: 100% auto;
  background-repeat: repeat;
}

.visual_menu--container-the_steak_house .visual_menu--page {
  font-size: 0.85em;
}
</style>

<style scoped>
.visual_menu--container-landscape .menu--section-wv_steak_house {
  padding: 0.5em 1em 3em 3em;
}

.visual_menu--container-landscape .menu--section-wv_steak_house {
  padding-top: 0.5em;
}

.menu--section-wv_steak_house:deep(.menu--section_title) {
  color: #000000;
  font-size: 1.6em;
  font-family: arno-pro, serif;
  padding: 0 2em 0.4em 0;
}

.menu--section-wv_steak_house:deep(.menu--section_subtitle) {
  font-style: italic;
  font-weight: initial;
  padding-right: 2em;
  text-align: center;
  letter-spacing: initial;
}

.visual_menu--section.menu--section-wv_steak_house:deep(.menu--section_subtitle) {
  font-size: 0.8em;
}

.menu--section-wv_steak_house:deep(.menu--section_subgroup_title) {
  font-weight: initial;
  color: #000000;
  margin: 0 0 -1em 0;
  font-size: 1.1em;
}

.menu--section-wv_steak_house:not(.menu--section-show_pricing_header):deep(.menu--section_subgroup_title) {
  padding-right: 2em;
}

.menu--section-wv_steak_house:deep(.menu--item_description) {
  font-style: italic;
  margin-left: 1.2em;
}

.menu--section-wv_steak_house:deep(.visual_menu--page_section) {
  text-align: center;
  font-style: italic;
  padding-right: 2em;
}

.menu--section-wv_steak_house:deep(.menu--item:not(.menu--item-price_inline, .menu--item-wv_steak_house_wine, .menu--item-highlighted_box)),
.menu--section-wv_steak_house:deep(.menu--item.menu--item-highlighted_box h3) {
  grid-template-columns: 1fr 0.25fr;
}

.menu--section-wv_steak_house:deep(.menu--item.menu--item-highlighted_box h3) {
  display: grid;
  grid-gap: 0.25em;
}

.menu--section-wv_steak_house:deep(.menu--item.menu--item-highlighted_box) {
  display: block;
  margin-left: -0.625em;
  margin-top: 0.25em;
}

.menu--section-wv_steak_house:deep(.menu--item-highlighted_box .menu--item_price) {
  margin-left: 0.8em;
}


.menu--section-wv_steak_house:deep(.menu--item_price) {
  text-align: left;
  padding-left: 2em;
}

.menu--section-wv_steak_house.menu--section-meat_temperature::before {
  content: '';
  position: absolute;
  top: 4.3em;
  left: 6em;
  right: 6em;
  height: 0.125em;
  background-color: #000000;
}

.menu--section-wv_steak_house.menu--section-meat_temperature:deep(.menu--item_title) {
  font-weight: 400;
}

.menu--section-wv_steak_house.menu--section-meat_temperature:deep(.menu--item) {
  display: block;
}

.menu--section-wv_steak_house.menu--section-meat_temperature.menu--section-two_column:deep(.menu--item_subgroup) {
  grid-gap: 0.25em;
}

.menu--section-wv_steak_house.menu--section-show_pricing_header:deep(.menu--item_subgroup-show-pricing .menu--section_subgroup_title span:nth-child(n + 2)) {
  text-align: left;
  padding-left: 2em;
}
</style>